/**
 * WizardChip - Selectable chip component for wizard steps
 * Used in Location type selection, Music genres/moods/tempo selection
 *
 * Supports two themes:
 * - Light (default): Gray background, dark text, dark selected state
 * - Dark: Transparent background, white border, white selected state
 */
import React from 'react';
import { Flex, Text, type FlexProps } from '@chakra-ui/react';

export interface WizardChipProps extends Omit<FlexProps, 'theme'> {
  label: string;
  icon?: React.ReactNode;
  selected?: boolean;
  theme?: 'light' | 'dark';
  size?: 'sm' | 'md' | 'lg';
  onPress?: () => void;
}

const sizeStyles = {
  sm: { px: 3, py: 2 },
  md: { px: 4, py: 3 },
  lg: { px: 5, py: '14px' },
};

export function WizardChip({
  label,
  icon,
  selected = false,
  theme = 'light',
  size = 'md',
  onPress,
  ...props
}: WizardChipProps) {
  const isLight = theme === 'light';
  const sizing = sizeStyles[size];

  const bg = selected
    ? isLight ? 'wizard.chipSelected' : 'wizard.chipSelectedDark'
    : isLight ? 'wizard.chipBg' : 'transparent';

  const borderColor = selected
    ? isLight ? 'wizard.chipSelected' : 'wizard.chipSelectedDark'
    : isLight ? 'wizard.chipBorder' : 'wizard.darkBorder';

  const textColor = selected
    ? isLight ? 'white' : 'wizard.dark'
    : isLight ? 'wizard.darkBorder' : 'white';

  return (
    <Flex
      align="center"
      justify="center"
      px={sizing.px}
      py={sizing.py}
      borderRadius="full"
      borderWidth="1px"
      borderColor={borderColor}
      bg={bg}
      cursor="pointer"
      transition="all 0.15s"
      _active={{ opacity: 0.8 }}
      onClick={onPress}
      userSelect="none"
      {...props}
    >
      {icon && <Flex mr={2}>{icon}</Flex>}
      <Text fontSize="14px" fontWeight="500" color={textColor}>
        {label}
      </Text>
    </Flex>
  );
}
